import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api'; 
import { isAxiosError } from 'axios';

export interface TemplatePreview {
  id: number;
  template_preview_img: string;
  urutan_preview: number;
  path?: string; 
}

export interface JenisAcaraTema {
  id: number;
  jenis_acara_id: number;
  image_sampul: string;
}

export interface ThemeItem {
  id: number;
  template_name: string;
  live_demo: string; 
  url_demo: string; 
  deskripsi: string;
  acara: boolean;
  custom: boolean; 
  previews: TemplatePreview[];
  jenis_acara_tema?: JenisAcaraTema[] | null; 
}
export interface RecommendCategory {
  id: number;
  template_recommend: string;
  deskripsi: string;
  list: {
    template: ThemeItem;
  }[];
}

export interface TemaAllResponse {
  message: string;
  pagination: {
    data: ThemeItem[];
    meta: { current_page: number; last_page: number; total: number };
  };
  recommend: RecommendCategory[];
}

export const useThemes = (
  acara_ids: string,
  category_ids: string,
  warna_ids: string,
  search: string,
  isReady: boolean 
) => {
  const [data, setData] = useState<TemaAllResponse | null>(null);
  const [allThemes, setAllThemes] = useState<ThemeItem[]>([]); 
  
  const [isLoading, setIsLoading] = useState(true); 
  const [isLoadingMore, setIsLoadingMore] = useState(false); 
  const [error, setError] = useState<string | null>(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchThemes = useCallback(async (page: number, isLoadMore: boolean = false) => {
    if (!isReady) return; 

    if (isLoadMore) {
        setIsLoadingMore(true);
    } else {
        setIsLoading(true);
        setAllThemes([]); 
        setHasMore(true);
    }
    setError(null);
    
    try {
      const query = new URLSearchParams();
      query.append('page', page.toString());
      query.append('limit', '25'); 
      
      if (acara_ids) query.append('acara_ids', acara_ids);
      if (category_ids) query.append('category_ids', category_ids);
      if (warna_ids) query.append('warna_ids', warna_ids);
      if (search) query.append('search', search);

      const response = await apiClient.get(`/api/list/tema-all?${query.toString()}`);
      const responseData = response.data as TemaAllResponse;
      
      setData(responseData);
      
      setAllThemes(prev => {
          const newItems = responseData.pagination.data || [];
          if (isLoadMore) {
             const existingIds = new Set(prev.map(item => item.id));
             const filteredNewItems = newItems.filter(item => !existingIds.has(item.id));
             return [...prev, ...filteredNewItems];
          }
          return newItems;
      });

      const meta = responseData.pagination.meta;
      if(meta) {
          setHasMore(meta.current_page < meta.last_page);
      } else {
          setHasMore(false);
      }
      
    } catch (err: unknown) {
      const message = isAxiosError<{ message?: string }>(err)
        ? err.response?.data?.message
        : undefined;
      setError(message || 'Gagal memuat daftar tema');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [acara_ids, category_ids, warna_ids, search, isReady]);

  useEffect(() => {
    if (isReady) {
      setCurrentPage(1); 
      fetchThemes(1, false);
    }
  }, [fetchThemes, isReady]);

  const loadMore = useCallback(() => {
    if (!isLoading && !isLoadingMore && hasMore && isReady) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchThemes(nextPage, true);
    }
  }, [isLoading, isLoadingMore, hasMore, isReady, currentPage, fetchThemes]);

  return { data, allThemes, isLoading, isLoadingMore, hasMore, error, loadMore };
};